import { useCallback, useEffect, useRef, useState } from "react";
import "@/App.css";
import Lenis from "lenis";
import { AnimatePresence, motion } from "framer-motion";
import { Toaster } from "sonner";
import LegalSetup from "@/components/legal/LegalSetup";
import ParticleField from "@/components/story/ParticleField";
import Hero from "@/components/story/Hero";
import FlyingApple from "@/components/story/FlyingApple";
import Story from "@/components/story/Story";
import ContactModal from "@/components/story/ContactModal";

function App() {
  const [started, setStarted] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const lenisRef = useRef(null);
  const pedestalRef = useRef(null);

  useEffect(() => {
    const lenis = new Lenis({ duration: 1.5, smoothWheel: true });
    lenisRef.current = lenis;
    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
    };
  }, []);

  useEffect(() => {
    if (!started) {
      lenisRef.current?.stop();
      document.body.style.overflow = "hidden";
    } else {
      lenisRef.current?.start();
      document.body.style.overflow = "";
    }
  }, [started]);

  const scrollTo = useCallback((target) => {
    lenisRef.current?.scrollTo(target, { duration: 2.2 });
  }, []);

  return (
    <LegalSetup lenisRef={lenisRef} started={started}>
      <div className="relative bg-white text-slate-900" data-testid="sypama-app">
      <ParticleField />
      <AnimatePresence>
        {!started && <Hero key="hero" onStart={() => setStarted(true)} />}
      </AnimatePresence>
      <FlyingApple started={started} pedestalRef={pedestalRef} />
      <AnimatePresence>
        {started && (
          <motion.div
            data-testid="side-trunk"
            className="pointer-events-none fixed right-0 top-0 z-[5] h-full w-[clamp(56px,7vw,110px)]"
            initial={{ clipPath: "inset(0 0 100% 0)", opacity: 0 }}
            animate={{ clipPath: "inset(0 0 0% 0)", opacity: 1 }}
            transition={{ duration: 2.2, delay: 0.35, ease: [0.16, 1, 0.3, 1] }}
          >
            <img
              src="/trunk.png"
              alt=""
              className="h-full w-full object-cover [mask-image:linear-gradient(transparent,black_9%,black_95%,transparent)]"
            />
          </motion.div>
        )}
      </AnimatePresence>
      <main className="relative z-10">
        <Story
          onContact={() => setContactOpen(true)}
          onExplore={() => scrollTo("#streams")}
          scrollTo={scrollTo}
          pedestalRef={pedestalRef}
        />
      </main>
      <ContactModal open={contactOpen} onOpenChange={setContactOpen} />
      <Toaster position="bottom-right" toastOptions={{ style: { fontFamily: "'Space Mono', monospace", fontSize: 12 } }} />
      </div>
    </LegalSetup>
  );
}

export default App;
